/**
 * Erro fatal → chamado para Sistemas.
 *
 * Duas peças puras, sem `window`: o buffer dos últimos erros do navegador
 * (o que aconteceu antes de a tela quebrar) e o texto que pré-preenche o
 * chamado aberto a partir da tela de erro. O envio continua sendo o do
 * `support-ticket.ts` — aqui só nasce o conteúdo.
 */

import { SUPPORT_TITLE_MAX } from './support-ticket'

export type ErrorLogKind = 'erro' | 'promessa' | 'console' | 'aviso' | 'tela' | 'carregamento'

export interface ErrorLogEntry {
  /** ISO 8601. */
  readonly at: string
  readonly kind: ErrorLogKind
  readonly message: string
  readonly stack?: string
}

export const ERROR_LOG_SIZE = 30
const MESSAGE_LIMIT = 500
const STACK_LIMIT = 4000

/**
 * Últimos `size` erros, do mais antigo ao mais novo; repetido seguido vira um só.
 *
 * `entries` pode ser um array de fora, alterado no lugar: o app passa o buffer
 * que o `index.html` já vinha enchendo antes de o bundle carregar.
 */
export class ErrorLog {
  constructor(
    private readonly size = ERROR_LOG_SIZE,
    private readonly entries: ErrorLogEntry[] = [],
  ) {}

  push(entry: ErrorLogEntry): void {
    const clean: ErrorLogEntry = {
      at: entry.at,
      kind: entry.kind,
      message: clip(entry.message, MESSAGE_LIMIT) || '(sem mensagem)',
      ...(entry.stack ? { stack: clip(entry.stack, STACK_LIMIT) } : {}),
    }
    const last = this.entries[this.entries.length - 1]
    if (last && last.kind === clean.kind && last.message === clean.message) {
      this.entries[this.entries.length - 1] = clean
      return
    }
    this.entries.push(clean)
    if (this.entries.length > this.size) this.entries.splice(0, this.entries.length - this.size)
  }

  list(): readonly ErrorLogEntry[] {
    return [...this.entries]
  }

  clear(): void {
    this.entries.length = 0
  }
}

/** Qualquer coisa lançada → mensagem e stack legíveis. */
export function describeThrown(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return { message: `${value.name && value.name !== 'Error' ? `${value.name}: ` : ''}${value.message}`, ...(value.stack ? { stack: value.stack } : {}) }
  }
  if (typeof value === 'string') return { message: value }
  try {
    return { message: JSON.stringify(value) ?? String(value) }
  } catch {
    return { message: String(value) }
  }
}

/** Chunk que sumiu depois de um deploy (Vite/Webpack, Chrome/Firefox/Safari). */
export function isChunkLoadError(value: unknown): boolean {
  const { message } = describeThrown(value)
  const name = value instanceof Error ? value.name : ''
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed/i.test(message)
  )
}

export interface ErrorContext {
  readonly message: string
  readonly stack?: string
  /** Pilha de componentes do React, quando veio de um boundary. */
  readonly componentStack?: string
  readonly route: string
  /** ISO 8601. */
  readonly at: string
  readonly userAgent: string
  readonly build?: string | null
  readonly log: readonly ErrorLogEntry[]
}

/** "21/10/2026 14:03:22", no fuso de quem viu o erro. */
export function formatErrorTime(iso: string, timeZone?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
    .format(date)
    .replace(', ', ' ')
}

/** "Chrome 141 · Windows" — o suficiente para o time de Sistemas reproduzir. */
export function browserSummary(userAgent: string): string {
  // Ordem importa: Edge e Chrome também dizem "Safari"; Edge também diz "Chrome".
  const known: readonly [string, RegExp][] = [
    ['Edge', /Edg\/(\d+)/],
    ['Firefox', /Firefox\/(\d+)/],
    ['Chrome', /Chrome\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari/],
  ]
  const hit = known.map(([label, re]) => [label, re.exec(userAgent)?.[1]] as const).find(([, v]) => v)
  const browser = hit ? `${hit[0]} ${hit[1]}` : 'Navegador desconhecido'
  const os = /Android/.test(userAgent)
    ? 'Android'
    : /iPhone|iPad/.test(userAgent)
      ? 'iOS'
      : /Windows/.test(userAgent)
        ? 'Windows'
        : /Mac OS X/.test(userAgent)
          ? 'macOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null
  return os ? `${browser} · ${os}` : browser
}

/** Relatório técnico completo: o bloco "Detalhes técnicos" e o "Copiar". */
export function errorReportText(ctx: ErrorContext, timeZone?: string): string {
  const lines = [
    `Mensagem: ${ctx.message}`,
    `Tela: ${ctx.route}`,
    `Horário: ${formatErrorTime(ctx.at, timeZone)}`,
    `Navegador: ${browserSummary(ctx.userAgent)}`,
    `User agent: ${ctx.userAgent}`,
    `Versão: ${ctx.build || 'não informada'}`,
    '',
    'Stack:',
    ctx.stack?.trim() || '(sem stack)',
  ]
  if (ctx.componentStack?.trim()) lines.push('', 'Componentes:', ctx.componentStack.trim())
  lines.push('', `Últimos erros no navegador (${ctx.log.length}):`)
  if (ctx.log.length === 0) lines.push('(nenhum)')
  for (const entry of ctx.log) lines.push(`[${formatErrorTime(entry.at, timeZone)}] ${entry.kind}: ${entry.message}`)
  return lines.join('\n')
}

/** Limite do Hub para a descrição; o pré-preenchimento deixa folga para a pessoa escrever. */
export const ERROR_TICKET_DESCRIPTION_BUDGET = 3000
const TITLE_LIMIT = SUPPORT_TITLE_MAX
// O chamado do PC não tem anexo: o que importa do erro vai no texto.
const STACK_LINES = 12
const LOG_LINES = 10

export interface ErrorTicketPrefill {
  readonly reason: 'erro'
  readonly title: string
  readonly description: string
}

/** Motivo, título e descrição do chamado aberto pela tela de erro. */
export function errorTicketPrefill(ctx: ErrorContext, timeZone?: string): ErrorTicketPrefill {
  const title = clip(`Erro na tela ${ctx.route.split('?')[0] || '/'}: ${ctx.message}`, TITLE_LIMIT)
  const stack = (ctx.stack ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, STACK_LINES)
  const recent = ctx.log.slice(-LOG_LINES)
  const head = [
    'O que eu estava fazendo: ',
    '',
    '— Registrado automaticamente pela tela de erro —',
    `Mensagem: ${clip(ctx.message, MESSAGE_LIMIT)}`,
    `Tela: ${ctx.route}`,
    `Horário: ${formatErrorTime(ctx.at, timeZone)}`,
    `Navegador: ${browserSummary(ctx.userAgent)}`,
    `Versão: ${ctx.build || 'não informada'}`,
  ]
  const tail: string[] = []
  if (stack.length) tail.push('', 'Stack (resumo):', ...stack)
  if (recent.length) {
    tail.push('', `Últimos erros (${recent.length} de ${ctx.log.length}):`)
    for (const e of recent) tail.push(`[${formatErrorTime(e.at, timeZone).slice(-8)}] ${e.kind}: ${clip(e.message, 200)}`)
  }
  const description = clip([...head, ...tail].join('\n'), ERROR_TICKET_DESCRIPTION_BUDGET)
  return { reason: 'erro', title, description }
}

function clip(value: string, max: number): string {
  const text = value.trim()
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}
